/**
 * Subida de VÍDEO resumable (F3-F). Selección de archivo (mp4/webm) → pre-vuelo
 * de decodificación + captura de duración/póster → subida multipart presignada
 * directa a R2 conducida por `mediaUploadMachine` (F2-B) con actores REALES
 * (F3-E). Progreso por partes, pausa/reanuda/cancela, persistencia en IndexedDB
 * para reanudar tras recarga, y superficie de errores de cuota/tamaño.
 */
import { type Component, createSignal, Match, onCleanup, Show, Switch } from "solid-js";
import { msg } from "@lingui/core/macro";
import { AlertTriangle, Film, Pause, Play, Upload, X } from "lucide-solid";
import { createActor } from "xstate";
import {
  type CompletedPart,
  mediaUploadMachine,
  type UploadFile,
  type VideoAssetMeta,
} from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { mediaApi, type MediaApi } from "../lib/media-api";
import { createUploadActors, PAUSE_SENTINEL } from "../lib/upload-actors";
import {
  ACCEPTED_VIDEO_TYPES,
  isAcceptedVideoType,
  probeVideo,
  VideoDecodeError,
} from "../lib/video-preflight";
import {
  clearPersistedUpload,
  loadPersistedUpload,
  resumeKeyFor,
  savePersistedUpload,
  toResumeState,
} from "../lib/upload-persistence";

export interface VideoUploadProps {
  courseId?: string | null;
  lessonId?: string | null;
  classSessionId?: string | null;
  api?: MediaApi;
  onUploaded: (asset: VideoAssetMeta) => void;
  onCancel?: () => void;
}

type Phase = "idle" | "probing" | "probeError" | "active" | "done";

export const VideoUpload: Component<VideoUploadProps> = (props) => {
  const { t } = useI18n();
  const api = () => props.api ?? mediaApi;

  const [phase, setPhase] = createSignal<Phase>("idle");
  const [machineValue, setMachineValue] = createSignal<string>("selecting");
  const [uploadedCount, setUploadedCount] = createSignal(0);
  const [totalParts, setTotalParts] = createSignal(0);
  const [errorMsg, setErrorMsg] = createSignal<string | null>(null);
  const [quotaError, setQuotaError] = createSignal(false);
  const [fileName, setFileName] = createSignal("");

  let paused = false;
  let actor: ReturnType<typeof createActor<typeof mediaUploadMachine>> | null = null;
  let subscription: { unsubscribe: () => void } | null = null;
  let posterBlob: Blob | null = null;

  const pct = () => {
    const total = totalParts();
    return total > 0 ? Math.min(100, Math.round((uploadedCount() / total) * 100)) : 0;
  };

  onCleanup(() => {
    subscription?.unsubscribe();
    actor?.stop();
  });

  const teardown = () => {
    subscription?.unsubscribe();
    subscription = null;
    actor?.stop();
    actor = null;
  };

  const reset = () => {
    teardown();
    paused = false;
    posterBlob = null;
    setPhase("idle");
    setMachineValue("selecting");
    setUploadedCount(0);
    setTotalParts(0);
    setErrorMsg(null);
    setQuotaError(false);
  };

  async function onFilePicked(file: File): Promise<void> {
    if (!isAcceptedVideoType(file)) {
      setPhase("probeError");
      setErrorMsg(t(msg`Only MP4 or WebM videos are supported.`));
      return;
    }
    setFileName(file.name);
    setPhase("probing");
    setErrorMsg(null);
    try {
      const probe = await probeVideo(file);
      posterBlob = probe.poster;
    } catch (err) {
      setPhase("probeError");
      if (err instanceof VideoDecodeError && err.reason === "timeout") {
        setErrorMsg(t(msg`The video took too long to analyze.`));
      } else if (err instanceof VideoDecodeError) {
        setErrorMsg(t(msg`This browser cannot play this video file.`));
      } else {
        setErrorMsg(t(msg`This file could not be processed.`));
      }
      return;
    }
    await startUpload(file);
  }

  async function startUpload(file: File): Promise<void> {
    const key = resumeKeyFor({
      courseId: props.courseId,
      lessonId: props.lessonId,
      classSessionId: props.classSessionId,
      fileName: file.name,
      sizeBytes: file.size,
    });
    const persisted = await loadPersistedUpload(key);
    const resume = persisted ? toResumeState(persisted) : null;

    const uploadFile: UploadFile = {
      name: file.name,
      size: file.size,
      type: file.type,
      blob: file,
    };

    let sendRef:
      | ((event: {
          type: "PROGRESS";
          uploadedCount: number;
          uploadedParts: CompletedPart[];
        }) => void)
      | null = null;

    const actors = createUploadActors({
      api: api(),
      context: {
        courseId: props.courseId,
        lessonId: props.lessonId,
        classSessionId: props.classSessionId,
      },
      shouldPause: () => paused,
      onProgress: (count, parts) => {
        setUploadedCount(count);
        sendRef?.({ type: "PROGRESS", uploadedCount: count, uploadedParts: parts });
      },
    });

    const machine = mediaUploadMachine.provide({
      actors,
      actions: {
        persist: ({ context }) => {
          if (context.assetId && context.uploadId) {
            void savePersistedUpload({
              resumeKey: key,
              assetId: context.assetId,
              uploadId: context.uploadId,
              uploadedParts: context.uploadedParts,
              fileName: file.name,
              sizeBytes: file.size,
              title: context.title,
              updatedAt: Date.now(),
            });
          }
        },
        clearPersisted: () => {
          void clearPersistedUpload(key);
        },
      },
    });

    teardown();
    setPhase("active");
    setQuotaError(false);
    setErrorMsg(null);

    actor = createActor(machine, {
      input: { file: uploadFile, title: file.name, resume },
    });
    sendRef = actor.send;

    // Suscripción EXTERNA (XState), no un scope reactivo de Solid: los setters
    // de señal aquí son el puente máquina→UI (patrón como addEventListener).
    // eslint-disable-next-line solid/reactivity
    subscription = actor.subscribe((snapshot) => {
      const value = typeof snapshot.value === "string" ? snapshot.value : "selecting";
      setMachineValue(value);
      setTotalParts(snapshot.context.totalParts);
      setUploadedCount(snapshot.context.uploadedCount);

      if (snapshot.matches("done")) {
        void finishUpload(snapshot.context.asset);
      } else if (snapshot.matches("failed")) {
        const message = snapshot.context.error ?? "";
        if (message === PAUSE_SENTINEL) {
          // Pausa intencional: no es error.
          setErrorMsg(null);
        } else {
          setErrorMsg(message);
          setQuotaError(/quota|413|storage/i.test(message));
        }
      } else if (snapshot.matches("aborted")) {
        reset();
        props.onCancel?.();
      }
    });

    actor.start();
    actor.send({ type: "START" });
  }

  async function finishUpload(asset: VideoAssetMeta | null): Promise<void> {
    if (!asset) {
      return;
    }
    // Sube el póster capturado (best-effort; no bloquea el "listo").
    if (posterBlob) {
      try {
        await api().uploadPoster(asset.id, posterBlob);
      } catch {
        /* póster opcional */
      }
    }
    setPhase("done");
    showToast(t(msg`Video uploaded`), "success");
    props.onUploaded(asset);
  }

  const isPaused = () => machineValue() === "failed" && paused;

  const pauseUpload = () => {
    paused = true;
  };
  const resumeUpload = () => {
    paused = false;
    actor?.send({ type: "RETRY" });
  };
  const retryUpload = () => {
    setErrorMsg(null);
    actor?.send({ type: "RETRY" });
  };
  const cancelUpload = () => {
    if (actor) {
      actor.send({ type: "ABORT" });
    } else {
      reset();
      props.onCancel?.();
    }
  };

  return (
    <div class="space-y-4" data-testid="video-upload">
      <Switch>
        {/* Selección */}
        <Match when={phase() === "idle" || phase() === "probeError"}>
          <label
            class="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border p-8 text-center transition-colors hover:border-primary/50"
            data-testid="video-upload-dropzone"
          >
            <Upload class="h-8 w-8 text-muted-foreground" />
            <span class="text-sm font-medium">{t(msg`Choose a video (MP4 or WebM)`)}</span>
            <span class="text-xs text-muted-foreground">
              {t(msg`Uploads resume automatically if interrupted.`)}
            </span>
            <input
              type="file"
              accept={ACCEPTED_VIDEO_TYPES}
              class="hidden"
              data-testid="video-file-input"
              onChange={(e) => {
                const f = e.currentTarget.files?.[0];
                if (f) {
                  void onFilePicked(f);
                }
                e.currentTarget.value = "";
              }}
            />
          </label>
          <Show when={phase() === "probeError"}>
            <p
              class="flex items-center gap-2 text-sm text-danger"
              data-testid="video-upload-decode-error"
            >
              <AlertTriangle class="h-4 w-4" /> {errorMsg()}
            </p>
          </Show>
        </Match>

        {/* Pre-vuelo */}
        <Match when={phase() === "probing"}>
          <div
            class="flex items-center gap-3 rounded-2xl border border-border p-4"
            data-testid="video-upload-probing"
          >
            <Film class="h-5 w-5 text-muted-foreground motion-safe:animate-pulse" />
            <span class="text-sm">{t(msg`Analyzing {name}…`, { name: fileName() })}</span>
          </div>
        </Match>

        {/* Subida activa / pausada / error */}
        <Match when={phase() === "active"}>
          <div class="space-y-3 rounded-2xl border border-border p-4">
            <div class="flex items-center gap-2 text-sm font-medium">
              <Film class="h-4 w-4 text-accent" />
              <span class="truncate" data-testid="video-upload-name">
                {fileName()}
              </span>
              <span
                class="ml-auto tabular-nums text-muted-foreground"
                data-testid="video-upload-pct"
              >
                {pct()}%
              </span>
            </div>
            <div class="h-2.5 overflow-hidden rounded-full bg-muted/60">
              <div
                class="h-full rounded-full bg-gradient-to-r from-accent to-joy-berry transition-all duration-300"
                style={{ width: `${pct()}%` }}
                data-testid="video-upload-bar"
              />
            </div>
            <p class="text-xs text-muted-foreground">
              {t(msg`Part {done} of {total}`, {
                done: uploadedCount(),
                total: totalParts() || "…",
              })}
            </p>

            <Show when={errorMsg()}>
              <div
                class="flex items-start gap-2 rounded-lg bg-danger/10 p-2 text-xs text-danger"
                data-testid="video-upload-error"
              >
                <AlertTriangle class="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  <Show when={quotaError()} fallback={errorMsg()}>
                    {t(msg`Storage quota exceeded for this organization.`)}
                  </Show>
                </span>
              </div>
            </Show>

            <div class="flex flex-wrap gap-2">
              <Show
                when={isPaused() || errorMsg()}
                fallback={
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="video-upload-pause"
                    onClick={pauseUpload}
                    class="gap-1.5"
                  >
                    <Pause class="h-4 w-4" /> {t(msg`Pause`)}
                  </Button>
                }
              >
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="video-upload-resume"
                  onClick={isPaused() ? resumeUpload : retryUpload}
                  class="gap-1.5"
                >
                  <Play class="h-4 w-4" /> {isPaused() ? t(msg`Resume`) : t(msg`Retry`)}
                </Button>
              </Show>
              <Button
                variant="ghost"
                size="sm"
                data-testid="video-upload-cancel"
                onClick={cancelUpload}
                class="gap-1.5 text-muted-foreground"
              >
                <X class="h-4 w-4" /> {t(msg`Cancel`)}
              </Button>
            </div>
          </div>
        </Match>

        {/* Listo */}
        <Match when={phase() === "done"}>
          <div
            class="flex items-center gap-2 rounded-2xl border border-success/40 bg-success/10 p-4 text-sm text-success"
            data-testid="video-upload-done"
          >
            <Film class="h-4 w-4" /> {t(msg`Video ready.`)}
          </div>
        </Match>
      </Switch>
    </div>
  );
};
